import { buildThermalTicketHtml, type ThermalPaper, type ThermalTicketData } from "@/lib/thermalInvoice";
import { printFullHtml, type PrintJobOptions } from "@/lib/printing";

export interface SampleCompany {
  name: string;
  rnc?: string | null;
  address?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  logoUrl?: string | null;
  headerText?: string | null;
  thanksText?: string | null;
  footerText?: string | null;
  returnText?: string | null;
}

/**
 * Factura de PRUEBA con datos realistas del taller (nombres largos, cantidades,
 * descuento, ITBIS y total). Sirve para validar formato, alineación y calidad
 * antes de conectar la venta real.
 */
export function buildSampleTicketData(company: SampleCompany, fiscalEnabled = false): ThermalTicketData {
  const items = [
    { description: "NEUMÁTICO MICHELIN PRIMACY 4 205/55 R16 91V", quantity: 4, unitPrice: 6500, discount: 0, lineTotal: 26000 },
    { description: "ACEITE MOTOR SINTÉTICO 5W30 1 GALÓN", quantity: 2, unitPrice: 2450, discount: 400, lineTotal: 4500 },
    { description: "FILTRO DE ACEITE TOYOTA COROLLA 2014-2019", quantity: 1, unitPrice: 850, discount: 0, lineTotal: 850 },
    { description: "SERVICIO DE ALINEACIÓN Y BALANCEO", quantity: 1, unitPrice: 1800, discount: 0, lineTotal: 1800 },
    { description: "BUJÍA IRIDIUM DENSO (JUEGO 4 UNIDADES)", quantity: 1, unitPrice: 1150, discount: 100, lineTotal: 1050 },
  ];

  const subtotal = 34700;
  const discountTotal = 500;
  const taxTotal = 6156;
  const total = 40356;
  const unitCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const now = new Date();

  return {
    company: {
      name: company.name,
      rnc: company.rnc,
      address: company.address,
      phone: company.phone,
      whatsapp: company.whatsapp,
      email: company.email,
      logoUrl: company.logoUrl,
      headerText: company.headerText ?? "Neumáticos & Servicios en General",
    },
    invoice: {
      number: "000001",
      documentTitle: fiscalEnabled ? "Factura de Crédito Fiscal" : "Factura",
      dateLabel: now.toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric" }),
      timeLabel: now.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" }),
      subtotal,
      discountTotal,
      taxTotal,
      total,
      unitCount,
    },
    items,
    customer: {
      name: "FARMACIA GENOSAN SRL (CLIENTE DE PRUEBA)",
      document: "1-32-05805-4",
      phone: "809-555-1234",
      address: "Av. Independencia #245, Santo Domingo",
      paymentLabel: "Efectivo",
    },
    cashier: "Cajero de Prueba",
    visibility: {
      showLogo: true,
      // El RNC del negocio solo se imprime cuando el ticket lleva comprobante fiscal.
      showRnc: fiscalEnabled,
      showAddress: true,
      showPhone: true,
      showSocial: true,
      showCustomer: true,
      showCashier: true,
      showDiscount: true,
      showTax: true,
      showNcf: fiscalEnabled,
      showThanks: true,
      showReturnMessage: true,
      thanksText: company.thanksText ?? "¡Gracias por su compra!",
      returnText: company.returnText ?? "Cambios y devoluciones dentro de 15 días con esta factura.",
      footerText: company.footerText ?? null,
    },
    fiscal: {
      enabled: fiscalEnabled,
      title: "Factura de Crédito Fiscal Electrónica",
      ncfLabel: "e-NCF",
      ncf: "E310000000500",
      ncfExpiry: "31/12/2027",
      securityCode: null,
      qrDataUrl: null,
      signedAt: null,
    },
  };
}

export function buildSampleTicketHtml(company: SampleCompany, paper: ThermalPaper, fiscalEnabled = false): string {
  return buildThermalTicketHtml(buildSampleTicketData(company, fiscalEnabled), paper);
}

/** Imprime la factura de prueba por la impresora configurada (silenciosa en Electron). */
export function printSampleTicket(
  company: SampleCompany,
  paper: ThermalPaper,
  fiscalEnabled = false,
  job: PrintJobOptions = {},
): void {
  printFullHtml(buildSampleTicketHtml(company, paper, fiscalEnabled), {
    kind: "ticket",
    widthMm: paper === "pos58" ? 58 : 80,
    ...job,
  });
}